
"use client";

import { useState, useEffect, useMemo, Suspense } from 'react';
import type { ArtItem, CollectionItemDetails, Artist } from '@/types';
import { ArtCard } from '@/components/art-card';
import { Header } from '@/components/header';
import { collection, getDocs, query, where, doc, getDoc, documentId, orderBy, getDocsFromCache } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/components/providers/auth-provider';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { LogIn, Users, Palette, DollarSign, LayoutGrid, List, Archive, CheckCircle } from 'lucide-react';
import Link from 'next/link';
import { Footer } from '@/components/footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArtList } from '@/components/artworks/list-view';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { useRouter, useSearchParams } from 'next/navigation';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';


const googleProvider = new GoogleAuthProvider();

// Fetches the user's profile which contains the collection of artwork IDs
async function getUserProfile(userId: string) {
    if (!userId) return null;
    
    const userDocRef = doc(db, 'users', userId);
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        return {
            id: userDocSnap.id,
            roles: userData.roles || [],
            myCollection: userData.myCollection || []
        }
    }
    return null;
}

// Fetches artworks based on an array of IDs
async function getArtworksByIds(ids: string[]): Promise<ArtItem[]> {
    if (ids.length === 0) return [];
    
    const artworksCol = collection(db, 'artworks');
    // Firestore 'in' queries are limited to 30 items. We need to batch.
    const batches = [];
    for (let i = 0; i < ids.length; i += 30) {
        const batchIds = ids.slice(i, i + 30);
        const q = query(artworksCol, where(documentId(), 'in', batchIds));
        batches.push(getDocs(q));
    }

    const artworkSnapshots = await Promise.all(batches);
    const artworksList: ArtItem[] = [];

    artworkSnapshots.forEach(snapshot => {
        snapshot.docs.forEach(doc => {
            const data = doc.data();

            let price = 0;
            if (typeof data.price === 'number') {
                price = data.price;
            } else if (typeof data.price === 'string') {
                price = Math.round(parseFloat(data.price.replace(/[^0-9.-]+/g, "")) * 100);
            }

            const imageUrls = data.imageUrls || [data.imageUrl || `https://placehold.co/600x800.png`];

            artworksList.push({
                id: doc.id,
                title: data.title,
                artistNames: data.artistNames || [],
                year: data.year,
                description: data.description,
                price: price,
                currency: data.currency,
                imageUrls: imageUrls,
                searchTerms: data.searchTerms || [],
                createdAt: data.createdAt?.toDate()?.toISOString() || new Date().toISOString(),
                status: data.status,
                visibility: data.visibility
            } as ArtItem);
        });
    });

    return artworksList;
}

async function getAllCollectionDetails(userId: string): Promise<CollectionItemDetails[]> {
    if (!userId) return [];

    const collectionRef = collection(db, 'user_collections');
    const q = query(collectionRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);

    const detailsList: CollectionItemDetails[] = [];
    querySnapshot.forEach(doc => {
        const data = doc.data();
        detailsList.push({
            id: doc.id,
            userId: data.userId,
            artworkId: data.artworkId,
            purchasePriceMinorUnit: data.purchasePriceMinorUnit,
            purchasePriceCurrency: data.purchasePriceCurrency,
            editionNumber: data.editionNumber,
            location: data.location,
            notes: data.notes,
            addedAt: data.addedAt?.toDate()?.toISOString() || new Date().toISOString(),
            purchaseDate: data.purchaseDate,
            archived: data.archived
        } as CollectionItemDetails);
    });
    return detailsList;
}

async function getArtistsByNames(names: string[]): Promise<Map<string, Artist>> {
    const artistMap = new Map<string, Artist>();
    if (names.length === 0) return artistMap;

    const artistsCol = collection(db, 'artists');
    const batches: string[][] = [];
    for (let i = 0; i < names.length; i += 10) {
        batches.push(names.slice(i, i + 10));
    }

    const artistQueries = batches.map(batch => {
        const q = query(artistsCol, where('name', 'in', batch));
        return getDocs(q);
    });

    const artistSnapshots = await Promise.all(artistQueries);

    artistSnapshots.forEach(snapshot => {
         snapshot.forEach(doc => {
            const artist = doc.data() as Artist;
            artistMap.set(artist.name, artist);
        });
    });
    
    return artistMap;
}


type MergedArtItem = ArtItem & Partial<CollectionItemDetails> & { addedAt: string, artists: Artist[] };

function MyCollectionPageComponent() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();

  const [artItems, setArtItems] = useState<MergedArtItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
      artworkCount: 0,
      artistCount: 0,
      totalValue: 0,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('addedAt-desc');
  const [viewMode, setViewMode] = useState<'gallery' | 'list'>('gallery');

  useEffect(() => {
    const fetchArt = async () => {
      if (user) {
        setLoading(true);
        try {
            const [userProfile, collectionDetails] = await Promise.all([
                getUserProfile(user.uid),
                getAllCollectionDetails(user.uid)
            ]);

            const myArtworkIds = userProfile?.myCollection || [];
            
            if (myArtworkIds.length === 0) {
              setArtItems([]);
              setStats({ artworkCount: 0, artistCount: 0, totalValue: 0 });
            } else {
              const userArtworks = await getArtworksByIds(myArtworkIds);

              const allArtistNames = [...new Set(userArtworks.flatMap(art => art.artistNames))];
              const artistsMap = await getArtistsByNames(allArtistNames);

              const detailsMap = new Map(collectionDetails.map(d => [d.artworkId, d]));

              const mergedArtworks = userArtworks.map(art => {
                  const detail = detailsMap.get(art.id);
                  const { id: detailId, ...restOfDetail } = detail || {};
                  return {
                      ...art,
                      ...restOfDetail,
                      addedAt: detail?.addedAt || art.createdAt,
                      artists: art.artistNames.map(name => artistsMap.get(name)).filter(Boolean) as Artist[],
                  }
              });
              
              setArtItems(mergedArtworks);

              // Calculate stats
              const artworkCount = mergedArtworks.filter(item => !item.archived).length;
              const artistCount = artistsMap.size;
              const totalValue = mergedArtworks.reduce((sum, art) => sum + (art.price || 0), 0);
              
              setStats({ artworkCount, artistCount, totalValue: totalValue / 100 });
            }
        } catch (error) {
            console.error("Failed to fetch collection.", error);
            setArtItems([]); // Clear items on error
        } finally {
            setLoading(false);
        }
      } else {
        setArtItems([]);
        setLoading(false);
      }
    }

    if (!authLoading) {
      fetchArt();
    }
  }, [user, authLoading]);

  const { activeItems, archivedItems } = useMemo(() => {
    let items = [...artItems];

    if (searchTerm) {
       const lowercasedTerm = searchTerm.toLowerCase();
       items = items.filter(item =>
            item.title.toLowerCase().includes(lowercasedTerm) ||
            item.artistNames.some(name => name.toLowerCase().includes(lowercasedTerm)) ||
            (item.artists && item.artists.some(artist => artist.aliases?.some(alias => alias.toLowerCase().includes(lowercasedTerm)))) ||
            (item.searchTerms && item.searchTerms.some(tag => tag.toLowerCase().includes(lowercasedTerm)))
        );
    }
    
    items.sort((a, b) => {
      switch (sortOption) {
        case 'title-asc':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
        case 'artist-asc':
          return (a.artistNames[0] || '').localeCompare(b.artistNames[0] || '');
        case 'artist-desc':
          return (b.artistNames[0] || '').localeCompare(a.artistNames[0] || '');
        case 'addedAt-asc':
          return new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime();
        case 'addedAt-desc':
        default:
          return new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime();
      }
    });

    return {
      activeItems: items.filter(item => !item.archived),
      archivedItems: items.filter(item => item.archived)
    };
  }, [searchTerm, sortOption, artItems]);

  const handleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error("Error signing in with Google", error);
    }
  };
  
  const totalValueFormatted = useMemo(() => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(stats.totalValue);
  }, [stats.totalValue]);

  const PageContent = ({ items }: { items: MergedArtItem[] }) => {
    if (items.length > 0) {
        return viewMode === 'gallery' ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                {items.map((item, index) => (
                    <ArtCard key={item.id} item={item} priority={index < 4} />
                ))}
            </div>
        ) : (
           <ArtList items={items.map(item => ({...item}))} />
        )
    }

    return (
        <div className="text-center py-16 border rounded-lg">
            <h3 className="text-xl font-semibold">Your Collection is Empty</h3>
            <p className="text-muted-foreground mt-2">You have not added any artwork to your collection yet.</p>
        </div>
    )
  }
  
  const StatsCards = () => {
    if (loading || authLoading) {
        return (
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Skeleton className="h-28 w-full" />
                <Skeleton className="h-28 w-full" />
            </div>
        )
    }

    if (!user) {
        return null;
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <Card className="col-span-1">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Collection Stats</CardTitle>
                    <Palette className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="flex justify-around items-center pt-2">
                    <div className="text-center">
                        <div className="text-2xl font-bold">{stats.artworkCount}</div>
                        <p className="text-xs text-muted-foreground">Artworks</p>
                    </div>
                     <Separator orientation="vertical" className="h-10" />
                    <div className="text-center">
                        <div className="text-2xl font-bold">{stats.artistCount}</div>
                        <p className="text-xs text-muted-foreground">Artists</p>
                    </div>
                </CardContent>
            </Card>
            <Card className="hidden md:block">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Public Value</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold">{totalValueFormatted}</div>
                     <p className="text-xs text-muted-foreground">Total of public prices</p>
                </CardContent>
            </Card>
        </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">
        <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row justify-between items-start gap-6 mb-8">
                <h2 className="text-3xl font-headline">My Collection</h2>
            </div>
             <div className="mb-8">
                <StatsCards />
            </div>

            {user && artItems.length > 0 && (
                <div className="flex flex-col md:flex-row gap-4 mb-8">
                    <div className="flex-1">
                        <Label htmlFor="search-collection" className="sr-only">Search Collection</Label>
                        <Input 
                            id="search-collection"
                            name="search-collection"
                            placeholder="Search your collection..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-4">
                        <Select value={sortOption} onValueChange={setSortOption}>
                        <SelectTrigger className="w-full md:w-[180px]" aria-label="Sort by">
                            <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="addedAt-desc">Date Added: Newest</SelectItem>
                            <SelectItem value="addedAt-asc">Date Added: Oldest</SelectItem>
                            <SelectItem value="title-asc">Title: A-Z</SelectItem>
                            <SelectItem value="title-desc">Title: Z-A</SelectItem>
                            <SelectItem value="artist-asc">Artist: A-Z</SelectItem>
                             <SelectItem value="artist-desc">Artist: Z-A</SelectItem>
                        </SelectContent>
                        </Select>
                        <div className="flex items-center gap-1">
                             <Button variant={viewMode === 'gallery' ? "secondary" : "outline"} size="icon" onClick={() => setViewMode('gallery')}>
                                <LayoutGrid className={cn("h-4 w-4", viewMode === 'gallery' && "text-primary")} />
                            </Button>
                            <Button variant={viewMode === 'list' ? "secondary" : "outline"} size="icon" onClick={() => setViewMode('list')}>
                                <List className={cn("h-4 w-4", viewMode === 'list' && "text-primary")} />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            
            {(authLoading || loading) ? (
                 <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <CardSkeleton key={i} />
                    ))}
                </div>
            ) : !user ? (
                 <div className="text-center py-16 border rounded-lg flex flex-col items-center gap-4">
                    <h3 className="text-xl font-semibold">Please Log In</h3>
                    <p className="text-muted-foreground mt-2 max-w-md">You need to be logged in to view your personal collection. Please log in to continue.</p>
                    <Button onClick={handleSignIn}>
                        <LogIn className="mr-2 h-4 w-4" />
                        Login with Google
                    </Button>
                </div>
            ) : (
                <>
                    <PageContent items={activeItems} />
                    {archivedItems.length > 0 && (
                        <div className="mt-16">
                            <div className="flex items-center gap-4 mb-8">
                               <Separator className="flex-1" />
                               <h3 className="text-2xl font-headline flex items-center gap-2 text-muted-foreground"><Archive className="h-6 w-6"/> Archived Items</h3>
                               <Separator className="flex-1" />
                            </div>
                           <PageContent items={archivedItems} />
                        </div>
                    )}
                </>
            )}

        </div>
      </main>
      <Footer />
    </div>
  );
}

export default function MyCollectionPage() {
    return (
        <Suspense fallback={<div>Loading...</div>}>
            <MyCollectionPageComponent />
        </Suspense>
    )
}

function CardSkeleton() {
  return (
    <div className="flex flex-col space-y-3">
      <Skeleton className="h-[350px] w-full rounded-lg" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-1/4" />
      </div>
    </div>
  )
}
